// Home › Performance, Blueprint only (BL-123; pages/ux-full screens/dash.js, components/dash.js), on
// DashboardPage (BL-189): one page that answers "How is the business doing?" over the dates picked,
// against the same dates a year before: the figures, revenue by month, the phones, then every
// technician's scorecard. A scorecard opens that technician's page, with the jobs behind it. Who sees
// which part is performance-model.ts's; each part is its own action's answer, checked on the server, and
// says its own state as a section. A technician with no office role sees only their own page, as their
// Home. Every part is a catalogued one: BarChart, ScoreCard, Figures, RowList, DataTable, Area.
import { useState } from 'react';
import type { DatePreset } from '@flowology/domain';
import { day, type OpenTarget } from '@flowology/ui-web';
import {
  Area,
  BarChart,
  Button,
  DataTable,
  Figures,
  Link,
  Notice,
  RowList,
  ScoreCard,
  Segmented,
  SelectField,
  TextField,
  type DataColumn,
  type Pen,
  type Row,
} from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { BOOKS_INCOME, REVENUE_WORDS } from '../shared/revenue-words.ts';
import {
  businessFigs,
  cardsShown,
  cardState,
  chartEnd,
  datesInput,
  dollars,
  monthChange,
  monthPeriods,
  ownPhoneFigs,
  ownScorecardOnly,
  pct,
  penFor,
  performanceTitle,
  PRESETS,
  rangeLine,
  revenueLine,
  seesBusiness,
  seesPhones,
  seesTechs,
  shortMoney,
  SORTS,
  stars,
  STATUS_WORDS,
  statusWaits,
  targetWords,
  teamFigs,
  techFigs,
  trendSummary,
  unitsOf,
  type Booking,
  type Business,
  type Card,
  type Detail,
  type Fig,
  type JobRow,
  type JobStatus,
  type MonthPair,
  type RangeOut,
  type Scorecards,
  type SortBy,
  type Targets,
  type Trend,
} from './performance-model.ts';
import './performance-page.css';

type Dates = { preset: DatePreset; custom: { from: string; to: string } };
type Q<T> = { data: T | null; error: string | null; reload: () => Promise<void> };

/** A section from its answer: loading, then failed with Try again, then what it draws. */
function part<T>(
  q: Q<T>,
  base: { id?: string; title: string; what: string },
  draw: (d: T) => Pick<DashboardSection, 'children' | 'count'>,
): DashboardSection {
  const retry = () => void q.reload();
  if (q.data == null)
    return q.error
      ? { ...base, status: 'failed', failedText: q.error, onRetry: retry }
      : { ...base, status: 'loading', onRetry: retry };
  return { ...base, ...draw(q.data) };
}

const pen = (n: number | null): Pen | null => (n ? (n as Pen) : null);

// ── the dates ──

function DateBar({
  dates,
  onDates,
  range,
}: {
  dates: Dates;
  onDates: (d: Dates) => void;
  range: RangeOut | null;
}) {
  const [from, setFrom] = useState(dates.custom.from);
  const [to, setTo] = useState(dates.custom.to);
  const pick = (preset: string) => onDates({ ...dates, preset: preset as DatePreset });
  return (
    <div className="bp-perf-dates">
      {/* the presets side by side; on a phone, one choice */}
      <div className="bp-perf-desk">
        <Segmented
          label="Dates"
          items={PRESETS.map((p) => ({ id: p.id, label: p.label }))}
          selectedKey={dates.preset}
          onSelectionChange={pick}
        />
      </div>
      <div className="bp-perf-phone">
        <SelectField
          label="Dates"
          value={dates.preset}
          options={PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          onChange={pick}
        />
      </div>
      {dates.preset === 'custom' && (
        <form
          className="bp-perf-custom"
          onSubmit={(e) => {
            e.preventDefault();
            onDates({ preset: 'custom', custom: { from, to } });
          }}
        >
          <TextField
            label="From"
            type="date"
            width="date"
            value={from}
            {...(to ? { max: to } : {})}
            onChange={setFrom}
          />
          <TextField
            label="To"
            type="date"
            width="date"
            value={to}
            {...(from ? { min: from } : {})}
            onChange={setTo}
          />
          <Button type="submit" isDisabled={!from || !to}>
            Show these dates
          </Button>
        </form>
      )}
      {range && <p className="bp-perf-sub">{rangeLine(range)}</p>}
    </div>
  );
}

const figures = (list: Fig[]) => (
  <Figures items={list.map((f) => ({ label: f.label, value: f.value, sub: f.sub }))} />
);

// ── revenue by month ──

const LEGEND = {
  value: 'Revenue',
  compare: 'The same month a year before',
  rest: 'The rest of that month',
  band: 'Your dates',
};

function Chart({
  months,
  label,
  value = LEGEND.value,
  layout,
}: {
  months: MonthPair[];
  label: string;
  /** What the value's bar is ("Their revenue"). */
  value?: string;
  layout?: 'rows';
}) {
  const end = chartEnd(months);
  return (
    <BarChart
      periods={monthPeriods(months)}
      label={label}
      format={shortMoney}
      legend={{ ...LEGEND, value }}
      {...(end ? { end } : {})}
      {...(layout ? { layout } : {})}
    />
  );
}

type MonthLine = MonthPair & { id: string };
const MONTH_COLUMNS: DataColumn<MonthLine>[] = [
  {
    id: 'month',
    label: 'Month',
    rowHeader: true,
    cell: (m) => ({
      text: `${m.label} ${m.year}`,
      ...(m.toDate ? { lines: [{ text: `to ${m.toDate.through}` }] } : {}),
    }),
  },
  { id: 'revenue', label: 'Revenue', align: 'end', cell: (m) => ({ text: dollars(m.revenueCents) }) },
  {
    id: 'before',
    label: 'A year before',
    align: 'end',
    cell: (m) => ({
      text: dollars(m.toDate ? m.toDate.yearBeforeCents : m.yearBeforeCents),
      ...(m.toDate ? { lines: [{ text: `${dollars(m.yearBeforeCents)} the whole month` }] } : {}),
    }),
  },
  {
    id: 'change',
    label: 'Change',
    align: 'end',
    cell: (m) => {
      const ch = monthChange(m);
      return { text: ch === null ? '—' : `${ch >= 0 ? '+' : '−'}${Math.abs(ch)} %` };
    },
  },
];

function TrendPart({ t, business }: { t: Trend; business: Business | null }) {
  const c = business?.current;
  return (
    <>
      <p className="bp-perf-sum">{trendSummary(t)}</p>
      {/* what it counts, in the words Reports uses (QA-714) */}
      <p className="bp-perf-sub">
        {REVENUE_WORDS} {BOOKS_INCOME}
      </p>
      <Chart months={t.months} label={`Revenue by month: ${trendSummary(t)}`} />
      <Area title="See the numbers" level={3}>
        <DataTable
          columns={MONTH_COLUMNS}
          rows={[...t.months].reverse().map((m) => ({ ...m, id: m.month }))}
          empty="No months in these dates."
        />
      </Area>
      {c && (
        <Figures
          size="sm"
          across={4}
          label="More figures"
          items={[
            {
              label: 'Opportunities',
              value: c.opportunities.toLocaleString('en-CA'),
              sub: `${c.sold} sold`,
            },
            {
              label: 'Memberships sold',
              value: String(c.membershipsSold),
              sub: `${pct(c.membershipConversion)} of customers without one`,
            },
            {
              label: 'Leads set by technicians',
              value: c.leadsSet === null ? '—' : String(c.leadsSet),
              sub:
                c.leadSalesCents === null
                  ? 'Not recorded yet'
                  : `${dollars(c.leadSalesCents)} sold from them`,
            },
            {
              label: 'Customer satisfaction',
              value: stars(c.rating, 2),
              sub: c.ratings
                ? `of 5, ${c.ratings} rating${c.ratings === 1 ? '' : 's'}`
                : 'No ratings in these dates',
            },
          ]}
        />
      )}
    </>
  );
}

// ── the phones ──

function Phones({ b }: { b: Booking }) {
  const target = Math.round(b.target * 100);
  const rows: Row[] = b.rows.map((x) => ({
    id: x.key ?? x.label,
    name: x.label,
    label: `${x.label}: booking rate ${pct(x.rate)}`,
    ...(x.you ? { after: { text: ' You' } } : {}),
    lines: [
      {
        text: `${x.booked.toLocaleString('en-CA')} of ${x.leads.toLocaleString('en-CA')} booked, ${x.calls.toLocaleString('en-CA')} calls`,
      },
      ...(x.rate !== null && x.rate < b.target ? [{ word: 'under target', tone: 'wait' as const }] : []),
    ],
    ...(x.rate !== null
      ? {
          bar: {
            value: Math.round(x.rate * 100),
            label: `${x.label}: booking rate ${pct(x.rate)}`,
            marks: [{ at: target, kind: 'target' as const, label: `Target ${pct(b.target)}` }],
          },
        }
      : {}),
    end: { figure: pct(x.rate) },
  }));
  return (
    <>
      <Figures
        size="sm"
        across={4}
        label="The calls"
        items={[
          { label: 'Calls', value: b.team.calls.toLocaleString('en-CA') },
          { label: 'Could book', value: b.team.leads.toLocaleString('en-CA') },
          { label: 'Booked', value: b.team.booked.toLocaleString('en-CA') },
          { label: 'Booking rate', value: pct(b.team.rate) },
        ]}
      />
      <p className="bp-perf-sub bp-perf-calls">
        {b.yearBeforeRate === null
          ? 'No calls to compare a year before.'
          : `${pct(b.yearBeforeRate)} the same days a year before.`}{' '}
        Target {pct(b.target)}.
      </p>
      <RowList
        rows={rows}
        label="Who took the calls"
        empty={b.hidden ? 'You took no calls in these dates.' : 'No calls taken in these dates.'}
        {...(b.hidden > 0
          ? {
              note: `The other ${b.hidden} ${b.hidden === 1 ? 'person’s row is theirs' : 'people’s rows are theirs'} and their manager’s to see.`,
            }
          : {})}
      />
    </>
  );
}

// ── a technician's scorecard ──

function line(c: Card): string {
  const state = cardState(c);
  return `${c.businessUnit ?? 'No jobs in these dates'}${state ? `, ${state.charAt(0).toLowerCase()}${state.slice(1)}` : ''}`;
}

function Cell({
  c,
  pen: n,
  targets,
  onOpen,
}: {
  c: Card;
  pen: number | null;
  targets: Targets;
  onOpen: () => void;
}) {
  const f = c.current;
  const head = { name: c.name, pen: pen(n), line: line(c) };
  if (!f.jobsCompleted && !f.opportunities && !f.revenueCents)
    return (
      <ScoreCard
        {...head}
        empty={c.awayUntil ? 'Away: no jobs in these dates' : 'No jobs in these dates'}
        open={{ onPress: onOpen }}
      />
    );
  return (
    <ScoreCard
      {...head}
      figure={{ label: 'Revenue', value: dollars(f.revenueCents), sub: revenueLine(c) }}
      meter={{
        head: 'Conversion',
        value: pct(f.conversion),
        ...(f.conversion === null
          ? { none: 'Too few to rate' }
          : {
              bar: {
                value: Math.round(f.conversion * 100),
                label: `Conversion ${pct(f.conversion)}`,
                marks: [
                  {
                    at: Math.round(targets.conversion * 100),
                    kind: 'target' as const,
                    label: `Target ${pct(targets.conversion)}`,
                  },
                ],
              },
            }),
      }}
      facts={[
        { label: 'Opportunities', value: String(f.opportunities) },
        { label: 'Leads set', value: f.leadsSet ?? '—' },
        {
          label: 'Per opportunity',
          value: f.opportunityAverageCents === null ? '—' : dollars(f.opportunityAverageCents),
        },
        { label: 'Lead sales', value: f.leadSalesCents === null ? '—' : dollars(f.leadSalesCents) },
        { label: 'Memberships', value: pct(f.membershipConversion) },
        { label: 'Rating', value: stars(f.rating) },
      ]}
      {...(f.grossProfitCents !== undefined
        ? {
            foot: (
              <>
                Gross profit <b>{dollars(f.grossProfitCents)}</b>,{' '}
                {pct(f.revenueCents ? f.grossProfitCents / f.revenueCents : null)}
              </>
            ),
          }
        : {})}
      open={{
        onPress: onOpen,
        label: `${c.name}: ${dollars(f.revenueCents)} revenue, conversion ${pct(f.conversion)}. Open their jobs`,
      }}
    />
  );
}

/** The full scorecard, a row a figure: a rate beside its bar against the target. */
function FullCard({ c, targets }: { c: Card; targets: Targets }) {
  const f = c.current;
  const plain = (id: string, name: string, value: string, sub: string): Row => ({
    id,
    name,
    end: { figure: value },
    ...(sub ? { lines: [{ text: sub }] } : {}),
  });
  const rate = (id: string, name: string, v: number | null, target: number, sub: string): Row => {
    const t = targetWords(v, target);
    const words = v === null ? sub || 'Too few to rate' : sub;
    return {
      id,
      name,
      end: { figure: pct(v) },
      ...(v !== null
        ? {
            bar: {
              value: Math.round(v * 100),
              label: `${name} ${pct(v)}`,
              marks: [
                { at: Math.round(target * 100), kind: 'target' as const, label: `Target ${pct(target)}` },
              ],
            },
          }
        : {}),
      lines: [
        ...(words ? [{ text: words }] : []),
        ...(t ? [t.under ? { word: t.text, tone: 'wait' as const } : { text: t.text }] : []),
      ],
    };
  };
  const rows: Row[] = [
    plain(
      'opportunities',
      'Opportunities',
      String(f.opportunities),
      `${f.sold} sold, ${f.jobsCompleted} jobs completed`,
    ),
    rate(
      'conversion',
      'Conversion rate',
      f.conversion,
      targets.conversion,
      f.opportunities ? `${f.sold} of ${f.opportunities} opportunities sold` : '',
    ),
    plain('revenue', 'Revenue', dollars(f.revenueCents), revenueLine(c)),
    plain(
      'average',
      'Opportunity average',
      f.opportunityAverageCents === null ? '—' : dollars(f.opportunityAverageCents),
      'What each opportunity sold, on average',
    ),
    rate(
      'memberships',
      'Membership conversion',
      f.membershipConversion,
      targets.memberships,
      f.membershipOpportunities
        ? `${f.membershipsSold} of ${f.membershipOpportunities} customers with no membership joined`
        : 'No customers without a membership',
    ),
    plain(
      'leads',
      'Leads set',
      f.leadsSet === null ? '—' : String(f.leadsSet),
      'Not recorded yet: the iPad’s “Set a lead” is still to come',
    ),
    plain(
      'lead-sales',
      'Sales from their leads',
      f.leadSalesCents === null ? '—' : dollars(f.leadSalesCents),
      'Sold by whoever took the lead',
    ),
    plain(
      'rating',
      'Customer satisfaction',
      f.rating === null ? '—' : `${stars(f.rating)} of 5`,
      f.ratings ? `From ${f.ratings} rating${f.ratings === 1 ? '' : 's'}` : 'No ratings in these dates',
    ),
    ...(f.grossProfitCents !== undefined
      ? [
          plain(
            'gross-profit',
            'Gross profit',
            dollars(f.grossProfitCents),
            `${pct(f.revenueCents ? f.grossProfitCents / f.revenueCents : null)} of their revenue. Seen by whoever may see margins`,
          ),
        ]
      : []),
  ];
  return <RowList rows={rows} label="The scorecard" />;
}

// ── the page ──

export function PerformanceScreen({
  nav,
  held,
  onOpen,
}: {
  nav: NavProps;
  held: readonly string[];
  /** Opens a job from a technician's list; absent (a technician's own Home), the rows don't open. */
  onOpen?: (t: OpenTarget) => void;
}) {
  const tz = nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(tz);
  const [dates, setDates] = useState<Dates>({
    preset: 'month',
    custom: { from: `${today.slice(0, 8)}01`, to: today },
  });
  const [tech, setTech] = useState<string | null>(null);
  const own = ownScorecardOnly(held);
  if (own)
    return (
      <TechPage
        nav={nav}
        staffId={nav.staffId ?? null}
        own
        dates={dates}
        onDates={setDates}
        {...(onOpen ? { onOpen } : {})}
      />
    );
  if (tech)
    return (
      <TechPage
        nav={nav}
        staffId={tech}
        dates={dates}
        onDates={setDates}
        {...(onOpen ? { onOpen } : {})}
        onBack={() => setTech(null)}
      />
    );
  return <Dashboard nav={nav} held={held} dates={dates} onDates={setDates} onTech={setTech} />;
}

function Dashboard({
  nav,
  held,
  dates,
  onDates,
  onTech,
}: {
  nav: NavProps;
  held: readonly string[];
  dates: Dates;
  onDates: (d: Dates) => void;
  onTech: (id: string) => void;
}) {
  const input = datesInput(dates.preset, dates.custom);
  const business = useQuery<Business>('reports.businessFigures', seesBusiness(held) ? input : null);
  const trend = useQuery<Trend>('reports.revenueTrend', seesBusiness(held) ? input : null);
  const phones = useQuery<Booking>('reports.booking', seesPhones(held) ? input : null);
  const cards = useQuery<Scorecards>('reports.techScorecards', seesTechs(held) ? input : null);
  const [sort, setSort] = useState<SortBy>('revenue');
  const [unit, setUnit] = useState<string | null>(null);
  const crumb = usePageCrumb(nav);
  const range = business.data?.range ?? cards.data?.range ?? phones.data?.range ?? null;
  const figs: Fig[] | null = seesBusiness(held)
    ? business.data
      ? businessFigs(business.data)
      : null
    : seesTechs(held)
      ? cards.data
        ? teamFigs(cards.data.technicians, phones.data)
        : null
      : phones.data
        ? ownPhoneFigs(phones.data)
        : null;
  const figsQ = seesBusiness(held) ? business : seesTechs(held) ? cards : phones;
  const chart =
    seesBusiness(held) &&
    part(trend, { title: 'Revenue by month', what: 'revenue by month' }, (t) => ({
      children: <TrendPart t={t} business={business.data} />,
    }));
  const allPhones = seesTechs(held);
  const calls =
    seesPhones(held) &&
    part(
      phones,
      {
        id: 'calls',
        title: allPhones ? 'Calls and booking' : 'The team’s calls, and yours',
        what: 'the calls',
      },
      (b) => ({ children: <Phones b={b} />, ...(allPhones ? { count: b.rows.length } : {}) }),
    );
  const techs =
    seesTechs(held) &&
    part(cards, { title: 'Technicians', what: 'the technicians' }, (s) => {
      const shown = cardsShown(s.technicians, sort, unit);
      const order = new Map(s.technicians.map((c, i) => [c.staffId, penFor(i)]));
      return {
        count: shown.length,
        children: (
          <>
            <div className="bp-perf-tools">
              <Segmented
                label="Sort by"
                items={SORTS.map((x) => ({ id: x.id, label: x.label }))}
                selectedKey={sort}
                onSelectionChange={(k) => setSort(k as SortBy)}
              />
              <div className="bp-perf-pick">
                <SelectField
                  label="Department"
                  value={unit ?? ''}
                  options={[
                    { value: '', label: 'All departments' },
                    ...unitsOf(s.technicians).map((u) => ({ value: u.id, label: u.name })),
                  ]}
                  onChange={(v) => setUnit(v || null)}
                />
              </div>
              <p className="bp-perf-sub bp-perf-whole">
                Conversion target {pct(s.targets.conversion)}, marked on each bar. Open a technician for their
                jobs.
              </p>
            </div>
            {shown.length ? (
              <ul className="bp-perf-cards" aria-label="Technicians">
                {shown.map((c) => (
                  <li key={c.staffId} className="bp-perf-card">
                    <Cell
                      c={c}
                      pen={order.get(c.staffId) ?? null}
                      targets={s.targets}
                      onOpen={() => onTech(c.staffId)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="bp-perf-sub">No technicians in that department.</p>
            )}
          </>
        ),
      };
    });
  // the business's chart beside the phones, then the technicians; the dispatcher's page has the
  // technicians first, then the phones
  const main = chart ? [chart] : [techs, calls].filter((s): s is DashboardSection => !!s);
  const side = chart && calls ? [calls] : undefined;
  const below = chart && techs ? [techs] : undefined;
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: performanceTitle(held),
            lede: 'Every figure is for the dates below.',
            ...(figs ? { figures: figures(figs) } : {}),
          }}
          bar={<DateBar dates={dates} onDates={onDates} range={range} />}
          {...(!figs && figsQ.error
            ? {
                notice: (
                  <Notice
                    title="The figures didn’t load."
                    actions={<Link onPress={() => void figsQ.reload()}>Try again</Link>}
                  >
                    {figsQ.error}
                  </Notice>
                ),
              }
            : {})}
          main={main}
          {...(side ? { side } : {})}
          {...(below ? { below } : {})}
        />
      </main>
    </div>
  );
}

// ── one technician ──

function TechPage({
  nav,
  staffId,
  own = false,
  dates,
  onDates,
  onOpen,
  onBack,
}: {
  nav: NavProps;
  /** Null: a technician's sign-in with no staff record. */
  staffId: string | null;
  own?: boolean;
  dates: Dates;
  onDates: (d: Dates) => void;
  onOpen?: (t: OpenTarget) => void;
  onBack?: () => void;
}) {
  const [tab, setTab] = useState<'completed' | 'sold'>('completed');
  const [unit, setUnit] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);
  const d = useQuery<Detail>(
    'reports.techDetail',
    staffId
      ? {
          staffId,
          ...datesInput(dates.preset, dates.custom),
          ...(unit ? { businessUnitId: unit } : {}),
          ...(type ? { jobTypeId: type } : {}),
          ...(status ? { status } : {}),
        }
      : null,
  );
  const card = d.data?.scorecard;
  const title = own ? 'Your scorecard' : (card?.name ?? 'A technician');
  const placeCrumb = usePageCrumb(nav);
  // an open technician: the place, Performance going back, then their name
  const crumb =
    onBack && placeCrumb
      ? [placeCrumb[0]!, { label: 'Performance', href: `#/${nav.view}`, onPress: onBack }, { label: title }]
      : placeCrumb;
  const filtered = !!(unit || type || status);
  const clear = () => {
    setUnit(null);
    setType(null);
    setStatus(null);
  };
  const lede = `${own && card ? `${card.name}${card.businessUnit ? `, ${card.businessUnit}` : ''}. ` : ''}${card && cardState(card) ? `${cardState(card)}. ` : ''}Every figure is for the dates below.`;
  const actions = (
    <>
      {onBack && <Button onPress={onBack}>Back to Performance</Button>}
      {own && <Link href="/tech/">Open the tech app</Link>}
    </>
  );
  const head = { ...(crumb ? { crumb } : {}), title, lede, actions };
  if (!staffId)
    return (
      <div className="app">
        <main>
          <PageBanners nav={nav} />
          <DashboardPage
            head={head}
            main={[
              {
                title: 'The scorecard',
                status: 'empty',
                emptyText: 'Your sign-in has no staff record, so there’s no scorecard to show.',
              },
            ]}
          />
        </main>
      </div>
    );
  const x = d.data;
  const who = own ? 'Your' : 'Their';
  const jobsList = x?.[tab];
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{ ...head, ...(x ? { figures: figures(techFigs(x.scorecard, x.targets)) } : {}) }}
          bar={<DateBar dates={dates} onDates={onDates} range={x?.range ?? null} />}
          {...(x
            ? {}
            : {
                waiting: {
                  status: d.error ? ('failed' as const) : ('loading' as const),
                  what: own ? 'your scorecard' : 'their scorecard',
                  ...(d.error ? { failedText: d.error } : {}),
                  onRetry: () => void d.reload(),
                },
              })}
          main={
            x && jobsList
              ? [
                  {
                    id: 'jobs',
                    title: `${who} jobs`,
                    children: (
                      <>
                        <div className="bp-perf-tools">
                          <Segmented
                            label="Which jobs"
                            items={(['completed', 'sold'] as const).map((k) => ({
                              id: k,
                              label: `${k === 'completed' ? 'Completed' : 'Sold'} ${x[k].total}`,
                            }))}
                            selectedKey={tab}
                            onSelectionChange={(k) => setTab(k as 'completed' | 'sold')}
                          />
                          <div className="bp-perf-pick">
                            <SelectField
                              label="Department"
                              value={unit ?? ''}
                              options={[
                                { value: '', label: 'All departments' },
                                ...x.choices.businessUnits.map((u) => ({ value: u.id, label: u.name })),
                              ]}
                              onChange={(v) => {
                                setUnit(v || null);
                                setType(null);
                              }}
                            />
                          </div>
                          <div className="bp-perf-pick">
                            <SelectField
                              label="Job type"
                              value={type ?? ''}
                              options={[
                                { value: '', label: 'All job types' },
                                ...x.choices.jobTypes.map((t) => ({ value: t.id, label: t.name })),
                              ]}
                              onChange={(v) => setType(v || null)}
                            />
                          </div>
                          <div className="bp-perf-pick">
                            <SelectField
                              label="Status"
                              value={status ?? ''}
                              options={[
                                { value: '', label: 'Any status' },
                                ...x.choices.statuses.map((s) => ({ value: s, label: STATUS_WORDS[s] })),
                              ]}
                              onChange={(v) => setStatus((v || null) as JobStatus | null)}
                            />
                          </div>
                        </div>
                        <p className="bp-perf-sub">
                          {jobsList.total} {tab} in {x.range.words}
                          {filtered ? ', filtered' : ''}: {dollars(jobsList.totalCents)}{' '}
                          {filtered && <Link onPress={clear}>Clear the filters</Link>}
                        </p>
                        <Jobs
                          key={`${tab}-${unit}-${type}-${status}`}
                          rows={jobsList.rows}
                          {...(onOpen ? { onOpen } : {})}
                        />
                      </>
                    ),
                  },
                ]
              : []
          }
          side={
            x
              ? [
                  {
                    id: 'scorecard',
                    title: 'The scorecard',
                    children: <FullCard c={x.scorecard} targets={x.targets} />,
                  },
                  {
                    id: 'months',
                    title: `${who} revenue by month`,
                    children: (
                      <Chart
                        months={x.months}
                        label={`${who} revenue by month`}
                        value={`${who} revenue`}
                        layout="rows"
                      />
                    ),
                  },
                ]
              : []
          }
        />
      </main>
    </div>
  );
}

/** A technician's jobs: the first five, then Show all; each opens its job, where it can. */
function Jobs({ rows, onOpen }: { rows: JobRow[]; onOpen?: (t: OpenTarget) => void }) {
  return (
    <RowList
      label="Jobs"
      first={5}
      empty="No jobs match. Try another department, job type or status, or wider dates."
      rows={rows.map((r) => ({
        id: `${r.jobId}-${r.date}`,
        name: `${r.number} ${r.customerName}`,
        ...(onOpen ? { onOpen: () => onOpen({ kind: 'job', id: r.jobId }) } : {}),
        word: { text: STATUS_WORDS[r.status], tone: statusWaits(r.status) ? 'wait' : 'muted' },
        lines: [{ text: `${day(r.date)}. ${r.jobType}${r.businessUnit ? `, ${r.businessUnit}` : ''}` }],
        end: {
          figure: dollars(r.amountCents),
          ...(r.grossProfitCents !== undefined ? { sub: `${dollars(r.grossProfitCents)} gross profit` } : {}),
        },
      }))}
    />
  );
}
